import React from 'react';
import { StatCard } from '../StatCard';
import { BotIcon, FileTextIcon, HardDriveIcon, PlugIcon, CalendarIcon, ClockIcon, ActivityIcon, RefreshCwIcon } from 'lucide-react';
interface SpaceData {
  id: string;
  name: string;
  chatbotCount: number;
  storageUsed: string;
  documentsCount: number;
  apiConnections: number;
  lastUpdated: string;
  createdAt?: string; // Added for creation date
}
interface Props {
  spaceData: SpaceData;
}
export const OverviewTab = ({
  spaceData
}: Props) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Calculate time difference for "time ago" display
  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  };
  return <div className="space-y-6 sm:space-y-8" data-id="element-1203">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-1204">
        <StatCard icon={BotIcon} label="Active Chatbots" value={spaceData.chatbotCount} subtext="Deployed & Running" color="accent" data-id="element-1205" />
        <StatCard icon={FileTextIcon} label="Total Documents" value={spaceData.documentsCount} subtext="Uploaded & Processed" color="accent" data-id="element-1206" />
        <StatCard icon={HardDriveIcon} label="Storage Used" value={spaceData.storageUsed} subtext="Total Space" color="accent" data-id="element-1207" />
        <StatCard icon={PlugIcon} label="Connected APIs" value={spaceData.apiConnections} subtext="External Sources" color="accent" data-id="element-1208" />
      </div>
      <div className="bg-surface-light border border-surface-border rounded-none" data-id="element-1209">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-border" data-id="element-1210">
          <h3 className="text-base sm:text-lg font-medium text-white font-mono" data-id="element-1211">
            Activity
          </h3>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-1212">
          {/* Creation & Last Update */}
          <div className="space-y-4" data-id="element-1213">
            <div className="flex items-start gap-3" data-id="element-1214">
              <div className="p-2 bg-surface rounded-none border border-surface-border" data-id="element-1215">
                <CalendarIcon size={18} className="text-accent-light" data-id="element-1216" />
              </div>
              <div data-id="element-1217">
                <h4 className="text-sm font-medium text-gray-400 font-mono uppercase tracking-wide mb-1" data-id="element-1218">
                  Created
                </h4>
                <p className="text-sm sm:text-base text-white" data-id="element-1219">
                  {formatDate(spaceData.createdAt || spaceData.lastUpdated)}
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1" data-id="element-1220">
                  {getTimeAgo(spaceData.createdAt || spaceData.lastUpdated)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-1221">
              <div className="p-2 bg-surface rounded-none border border-surface-border" data-id="element-1222">
                <RefreshCwIcon size={18} className="text-accent-light" data-id="element-1223" />
              </div>
              <div data-id="element-1224">
                <h4 className="text-sm font-medium text-gray-400 font-mono uppercase tracking-wide mb-1" data-id="element-1225">
                  Last Updated
                </h4>
                <p className="text-sm sm:text-base text-white" data-id="element-1226">
                  {formatDate(spaceData.lastUpdated)}
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1" data-id="element-1227">
                  {getTimeAgo(spaceData.lastUpdated)}
                </p>
              </div>
            </div>
          </div>
          {/* Activity Stats */}
          <div className="space-y-4" data-id="element-1228">
            <div className="flex items-start gap-3" data-id="element-1229">
              <div className="p-2 bg-surface rounded-none border border-surface-border" data-id="element-1230">
                <ActivityIcon size={18} className="text-accent-light" data-id="element-1231" />
              </div>
              <div data-id="element-1232">
                <h4 className="text-sm font-medium text-gray-400 font-mono uppercase tracking-wide mb-1" data-id="element-1233">
                  Recent Activity
                </h4>
                <div className="space-y-2" data-id="element-1234">
                  <p className="text-sm sm:text-base text-white" data-id="element-1235">
                    Last document added: {getTimeAgo(spaceData.lastUpdated)}
                  </p>
                  <p className="text-sm sm:text-base text-white" data-id="element-1236">
                    Last chatbot interaction:{' '}
                    {getTimeAgo(spaceData.lastUpdated)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-1237">
              <div className="p-2 bg-surface rounded-none border border-surface-border" data-id="element-1238">
                <ClockIcon size={18} className="text-accent-light" data-id="element-1239" />
              </div>
              <div data-id="element-1240">
                <h4 className="text-sm font-medium text-gray-400 font-mono uppercase tracking-wide mb-1" data-id="element-1241">
                  Uptime Stats
                </h4>
                <div className="space-y-2" data-id="element-1242">
                  <p className="text-sm sm:text-base text-white" data-id="element-1243">
                    Average response time: 145ms
                  </p>
                  <p className="text-sm sm:text-base text-white" data-id="element-1244">
                    Chatbot availability: 99.9%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};